import { InstagramQuickExportSettings } from '@app/modules/social-export/components/settings/platforms/InstagramQuickExportSettings';
import { YoutubeQuickExportSettings } from '@app/modules/social-export/components/settings/platforms/YoutubeQuickExportSettings';
import {
  SocialPlatform,
  SocialPlatformId,
} from '@app/modules/social-export/domain/platforms';
import {
  BaseExportFormProps,
  BaseSettingsFormProps,
} from '@app/modules/social-export/hooks/social-export-form-props';
import { DailymotionConnect } from '@app/modules/social-export/platforms/dailymotion/connect/DailymotionConnect';
import { DailymotionPublishForm } from '@app/modules/social-export/platforms/dailymotion/DailymotionPublishForm';
import { DailymotionSettingsForm } from '@app/modules/social-export/platforms/dailymotion/DailymotionSettingsForm';
import { DemoPublishForm } from '@app/modules/social-export/platforms/demo/DemoPublishForm';
import { DemoSettingsForm } from '@app/modules/social-export/platforms/demo/DemoSettingsForm';
import { FacebookConnect } from '@app/modules/social-export/platforms/facebook/connect/FacebookConnect';
import { FacebookPublishForm } from '@app/modules/social-export/platforms/facebook/FacebookPublishForm';
import { FacebookSettingsForm } from '@app/modules/social-export/platforms/facebook/FacebookSettingsForm';
import { InstagramPublishForm } from '@app/modules/social-export/platforms/instagram/InstagramPublishForm';
import { InstagramSettingsForm } from '@app/modules/social-export/platforms/instagram/InstagramSettingsForm';
import { TikTokPublishForm } from '@app/modules/social-export/platforms/tiktok/TikTokPublishForm';
import { TikTokSettingsForm } from '@app/modules/social-export/platforms/tiktok/TikTokSettingsForm';
import { TwitterPublishForm } from '@app/modules/social-export/platforms/twitter/TwitterPublishForm';
import { TwitterSettingsForm } from '@app/modules/social-export/platforms/twitter/TwitterSettingsForm';
import { YoutubeConnect } from '@app/modules/social-export/platforms/youtube/connect/YoutubeConnect';
import { YoutubePublishForm } from '@app/modules/social-export/platforms/youtube/YoutubePublishForm';
import { YoutubeSettingsForm } from '@app/modules/social-export/platforms/youtube/YoutubeSettingsForm';
import { ComponentType } from 'react';

/** Shared publish-form component type used by the platform registry and upload flow. */
export type PlatformPublishFormComponent = ComponentType<
  BaseExportFormProps<Record<string, unknown>, unknown>
>;

/** Shared settings-form component type used by the platform registry and settings page. */
export type PlatformSettingsFormComponent = ComponentType<BaseSettingsFormProps<unknown>>;

export type PlatformExportDescriptor = {
  PublishForm: PlatformPublishFormComponent;
  SettingsForm: PlatformSettingsFormComponent;
  /** Custom connect UI; when omitted, DefaultPlatformConnect is used. */
  Connect?: ComponentType;
  /** Optional quick-create settings rendered when the platform toggle is enabled. */
  QuickExportSettings?: ComponentType<{ fieldPrefix?: string }>;
};

/**
 * Narrows a platform-specific publish form into the shared registry type.
 * Heterogeneous DTOs cannot share one generic without a boundary cast.
 */
function asPublishForm<Data, Settings>(
  form: ComponentType<BaseExportFormProps<Data, Settings>>,
): PlatformPublishFormComponent {
  return form as PlatformPublishFormComponent;
}

/**
 * Narrows a platform-specific settings form into the shared registry type.
 */
function asSettingsForm<Data>(
  form: ComponentType<BaseSettingsFormProps<Data>>,
): PlatformSettingsFormComponent {
  return form as PlatformSettingsFormComponent;
}

/**
 * Single registry of platform publish/settings/connect/quick-export components.
 */
export const platformExportDescriptors: Record<SocialPlatformId, PlatformExportDescriptor> = {
  YOUTUBE: {
    PublishForm: asPublishForm(YoutubePublishForm),
    SettingsForm: asSettingsForm(YoutubeSettingsForm),
    Connect: YoutubeConnect,
    QuickExportSettings: YoutubeQuickExportSettings,
  },
  TWITTER: {
    PublishForm: asPublishForm(TwitterPublishForm),
    SettingsForm: asSettingsForm(TwitterSettingsForm),
  },
  INSTAGRAM: {
    PublishForm: asPublishForm(InstagramPublishForm),
    SettingsForm: asSettingsForm(InstagramSettingsForm),
    QuickExportSettings: InstagramQuickExportSettings,
  },
  DAILYMOTION: {
    PublishForm: asPublishForm(DailymotionPublishForm),
    SettingsForm: asSettingsForm(DailymotionSettingsForm),
    Connect: DailymotionConnect,
  },
  FACEBOOK: {
    PublishForm: asPublishForm(FacebookPublishForm),
    SettingsForm: asSettingsForm(FacebookSettingsForm),
    Connect: FacebookConnect,
  },
  TIKTOK: {
    PublishForm: asPublishForm(TikTokPublishForm),
    SettingsForm: asSettingsForm(TikTokSettingsForm),
  },
  DEMO: {
    PublishForm: asPublishForm(DemoPublishForm),
    SettingsForm: asSettingsForm(DemoSettingsForm),
  },
};

/**
 * Looks up the export descriptor for a platform singleton.
 */
export function getPlatformExportDescriptor(platform: SocialPlatform): PlatformExportDescriptor {
  const descriptor = platformExportDescriptors[platform.id];
  if (!descriptor) {
    throw new Error(`Platform ${platform.id} not implemented!`);
  }
  return descriptor;
}
